import { Command } from "commander";
import { readFileSync, existsSync } from "node:fs";
import { resolve } from "node:path";
import chalk from "chalk";
import Table from "cli-table3";
import { getProjectName } from "../project-name.js";
import { fatalError } from "../ux/errors.js";

interface TrackedSession {
  featureId?: string;
  status: string;
  inputTokens: number;
  outputTokens: number;
}

export const statusCommand = new Command("status")
  .description("Show project status from registry + tracking data")
  .option("-p, --project <name>", "Project name")
  .option("--registry <path>", "Path to registry.json")
  .option("--json", "Output machine-readable JSON")
  .action((options: { project?: string; registry?: string; json?: boolean }) => {
    const registryPath =
      options.registry ??
      resolve(process.cwd(), ".fathom", "registry.json");

    if (!existsSync(registryPath)) {
      console.log(
        chalk.yellow(
          "No registry found. Run `fathom analyze <spec> --project <name>` first.",
        ),
      );
      return;
    }

    let registry: { project?: string; features: Array<{ id: string; name: string; estimated_tokens: number; status: string }> };
    try {
      registry = JSON.parse(readFileSync(registryPath, "utf-8"));
    } catch {
      fatalError("Invalid registry", "Failed to parse " + registryPath, "Delete and re-run fathom analyze");
    }
    const projectName = options.project ?? registry.project ?? getProjectName();

    // Load tracking data if available
    const summaryPath = resolve(
      process.cwd(),
      ".fathom",
      "tracking",
      "summary.json",
    );
    const hasTracking = existsSync(summaryPath);
    const actuals = new Map<string, { tokens: number; sessions: number }>();

    if (hasTracking) {
      let summary: { sessions: TrackedSession[] };
      try {
        summary = JSON.parse(readFileSync(summaryPath, "utf-8"));
      } catch {
        fatalError("Invalid tracking data", "Failed to parse " + summaryPath, "Delete and re-run fathom track --backfill");
      }
      for (const s of summary.sessions as TrackedSession[]) {
        if (!s.featureId || s.status === "untagged") continue;
        const existing = actuals.get(s.featureId) ?? { tokens: 0, sessions: 0 };
        existing.tokens += s.inputTokens + s.outputTokens;
        existing.sessions += 1;
        actuals.set(s.featureId, existing);
      }
    }

    // JSON output mode
    if (options.json) {
      const features = registry.features.map(f => {
        const actual = actuals.get(f.id);
        return {
          name: f.name,
          status: f.status,
          estimated_tokens: f.estimated_tokens,
          ...(actual ? { actual_tokens: actual.tokens, sessions: actual.sessions } : {}),
        };
      });
      console.log(JSON.stringify({ project: projectName, features }, null, 2));
      return;
    }

    console.log(chalk.bold(`\nFathom — Status: ${projectName}`));
    console.log(chalk.dim("─".repeat(65)));

    const head = [
      chalk.white("Feature"),
      chalk.white("Status"),
      chalk.white("Est. Tokens"),
    ];
    const colAligns: Array<"left" | "center" | "right"> = ["left", "center", "right"];

    if (hasTracking) {
      head.push(chalk.white("Actual"), chalk.white("Sessions"), chalk.white("Progress"));
      colAligns.push("right", "center", "center");
    }

    const table = new Table({ head, colAligns });

    let totalEstimated = 0;
    let totalActual = 0;

    for (const f of registry.features) {
      totalEstimated += f.estimated_tokens;

      const statusIcon =
        f.status === "complete"
          ? chalk.green("done")
          : f.status === "in-progress"
            ? chalk.yellow("active")
            : chalk.dim("pending");

      const row: string[] = [
        f.name,
        statusIcon,
        f.estimated_tokens.toLocaleString(),
      ];

      if (hasTracking) {
        const actual = actuals.get(f.id);
        if (actual) {
          totalActual += actual.tokens;
          const pct = Math.round((actual.tokens / f.estimated_tokens) * 100);
          const pctColor = pct > 100 ? chalk.red : pct > 75 ? chalk.yellow : chalk.green;
          row.push(
            actual.tokens.toLocaleString(),
            actual.sessions.toString(),
            pctColor(`${pct}%`),
          );
        } else {
          row.push(chalk.dim("—"), chalk.dim("0"), chalk.dim("—"));
        }
      }

      table.push(row);
    }

    console.log(table.toString());

    console.log(chalk.dim(`\nTotal estimated: ${totalEstimated.toLocaleString()} tokens`));
    if (hasTracking && totalActual > 0) {
      const overallPct = Math.round((totalActual / totalEstimated) * 100);
      console.log(chalk.dim(`Total actual:    ${totalActual.toLocaleString()} tokens (${overallPct}% of budget)`));
    }
    console.log(chalk.dim(`Features: ${registry.features.length}\n`));
  });
